import * as React from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { Action, Store } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { match, RouteComponentProps } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import * as querystring from 'query-string';
import cn from 'classnames';
import { Venues, VendorsApi } from '@weddingspot/ws-api-client';
import {
    FeatureFlagContext,
    FLAG_SHOPPING_CART_FOR_SRP,
    formatCventImage,
    HeaderContext,
    LoadingSpinner,
    LocalStorage,
    StorableDataKeys,
    UserInfo,
    WSAppContext,
} from '@weddingspot/ws-core-components';

import * as types from '../types';
import * as CONFIG from '../constants/config';
import { IS_NODE_SERVER } from '../constants/config';
import * as analytics from '@weddingspot/ws-analytics';

import {
    CeremonyServiceTypes,
    IndoorVenueServiceTypes,
    OutdoorVenueServiceTypes,
    ReceptionServiceTypes,
    VenueImageData,
    VenueProfile as VenueProfileProps,
    VenueService,
} from '../types/venue-profile';

import { tryGetUserInfo } from '../actions/auth';
import { addServerError, ErrorAction } from '../actions/errors';
import {
    closeMoreTab,
    hideInquiryModal,
    loadPersonalizedVenueDetails,
    loadUnlistedVenue,
    loadVenueDetails,
    openMoreTab,
    renderRdvProfile,
    setActiveTab,
    setVenueFavorite,
    showInquiryModal,
    showPhoneNumber,
    slideToNextImage,
    slideToPrevImage,
    tryLoadRecommendations,
    unsetVenueFavorite,
    VenueProfileAction,
} from '../actions/venue-profile';
import { navigatedAwayFromVenueProfile, navigatedToVenueProfile } from '../actions/navigation';

import VenueProfile from '../components/venue-profile-new';
import BlurryLoader from '../components/BlurryLoader';
import { ServiceProps } from '../components/service-options-modal/Services';
import { VenueProfileRouteAction } from '../urls/routes';
import { UnlistedVenuePage } from '../components/unlisted-venue-page';
import { ComponentForSSR } from '../types/ComponentForSSR';
import { EventParams } from '@weddingspot/ws-analytics';

const { AnalyticsEvent, EventLocation, InquiryType, logEvent, setCurUserProps, UserProp } = analytics;

const VENUE_PAGE_APP_CLASS = 'App__VenuePage';
const UNLISTED_VENUE_PAGE_APP_CLASS = 'App__UnlistedVenuePage';

export interface RouteMatchProps {
    id?: string;
    name?: string;
    action?: string;
}

export interface Props extends VenueProfileProps, RouteComponentProps<RouteMatchProps> {
    dispatchLoadVenueDetails: (venueId: number, params?: Venues.VenueDetailsQueryParams) => Promise<void>;
    dipsatchLoadPersonalizedDetails: (venueId: number) => Promise<void>;
    slideToNextImage: () => void;
    slideToPrevImage: () => void;
    showPhone: () => void;
    setActiveTab: (tabIndex: number) => void;
    openMoreTab: () => void;
    closeMoreTab: () => void;
    setFavorite: (venueId: number, favoriteType?: Venues.FavoriteType) => Promise<void>;
    unsetFavorite: (venueId: number) => Promise<void>;
    raiseServerError: () => void;
    navigatedToVenueProfile: (venueId: number) => void;
    navigatedAwayFromVenueProfile: () => void;
    getVenueHeaderData: (venueId: number, isRDFlow: boolean) => Promise<void>;
    loadRecommendations: (venueId: number, isRdv: boolean, excludeIds?: number[]) => Promise<void>;
    showSigninModal: () => void;
    showShoppingCart?: boolean;
    tryGetUserInfo: () => void;
    showInquiryModal: () => void;
    hideInquiryModal: () => void;
    renderRdvProfile: () => void;

    // venueId: number;
    isAuthed: boolean;
    userInfo: UserInfo | null;
}

const mapStateToProps = ({ venueProfile }: types.StoreState) => ({
    ...venueProfile,
});

const loadVenueDetailsDispatcher = (
    dispatch: ThunkDispatch<types.StoreState, undefined, VenueProfileAction | ErrorAction>,
    venueId: number,
    params?: Venues.VenueDetailsQueryParams
) => {
    return VendorsApi.venueDetails(venueId, params)
        .then((ret) => {
            if (ret.is_not_available) {
                dispatch(loadUnlistedVenue(ret as unknown as Venues.UnlistedVenueDetails));
            } else {
                dispatch(loadVenueDetails(ret as Venues.VenueDetails));
                const { has_weddings, has_rds } = ret as Venues.VenueDetails;
                const queryStringHasRdv = params?.rdv;

                if (!has_weddings && !has_rds) throw { status: 404, additionalInfo: 'case1: no has_weddings and no has_rds' };
                if (!has_weddings && !queryStringHasRdv)
                    throw { status: 404, additionalInfo: 'case2: no has_weddings and  no queryStringHasRdv' };
                if (!has_rds && queryStringHasRdv) throw { status: 404, additionalInfo: 'case3: no has_rds and queryStringHasRdv' };
            }
        })
        .catch((err) => {
            dispatch(addServerError(err.status));
            throw err;
        });
};

const dipsatchLoadPersonalizedDetails = (dispatch: ThunkDispatch<types.StoreState, undefined, VenueProfileAction>, venueId: number) => {
    return VendorsApi.personalizedVenueDetails(venueId)
        .then((ret) => {
            dispatch(loadPersonalizedVenueDetails(ret));
        })
        .catch(() => {
            // Do nothing since it's likely user won't be authenticated and we don't care if this fails
        });
};

const mapDispatchToProps = (dispatch: ThunkDispatch<types.StoreState, undefined, Action>) => ({
    dispatchLoadVenueDetails: (venueId: number, params?: Venues.VenueDetailsQueryParams) => {
        return loadVenueDetailsDispatcher(dispatch, venueId, params);
    },
    dipsatchLoadPersonalizedDetails: (venueId: number) => {
        return dipsatchLoadPersonalizedDetails(dispatch, venueId);
    },
    slideToNextImage: () => dispatch(slideToNextImage()),
    slideToPrevImage: () => dispatch(slideToPrevImage()),
    showPhone: () => dispatch(showPhoneNumber()),
    setActiveTab: (tabIndex: number) => dispatch(setActiveTab(tabIndex)),
    openMoreTab: () => dispatch(openMoreTab()),
    closeMoreTab: () => dispatch(closeMoreTab()),
    setFavorite: (venueId: number, favoriteType?: Venues.FavoriteType) => {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_FAVORITES, {
            'Venue ID': venueId,
            Location: EventLocation.PROFILE,
            Value: 'Select',
        });
        return VendorsApi.setFavorite([venueId], favoriteType).then((_) => dispatch(setVenueFavorite()));
    },
    unsetFavorite: (venueId: number) => {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_FAVORITES, {
            'Venue ID': venueId,
            Location: EventLocation.PROFILE,
            Value: 'Deselect',
        });
        return VendorsApi.removeFavorite([venueId]).then((_) => dispatch(unsetVenueFavorite()));
    },
    raiseServerError: () => {
        dispatch(addServerError());
    },
    navigatedToVenueProfile: (venueId: number) => dispatch(navigatedToVenueProfile(venueId)),
    navigatedAwayFromVenueProfile: () => dispatch(navigatedAwayFromVenueProfile()),
    loadRecommendations: (venueId: number, isRdv: boolean, excludeIds?: number[]) =>
        dispatch(tryLoadRecommendations(venueId, isRdv, excludeIds)),
    tryGetUserInfo: () => {
        dispatch(tryGetUserInfo());
    },
    showInquiryModal: () => dispatch(showInquiryModal()),
    hideInquiryModal: () => dispatch(hideInquiryModal()),
    renderRdvProfile: () => dispatch(renderRdvProfile()),
});

/**
 * Given the router match, parse an integer out of it
 */
const parseIdFromMatch = (match: match<RouteMatchProps> | null): number | undefined => {
    if (!match || !match.params.id) {
        return undefined;
    }

    let parsed = parseInt(match.params.id, 10);
    if (parsed !== NaN) {
        return parsed;
    }

    return undefined;
};

export class _VenueProfileContainer extends ComponentForSSR<Props, {}> {
    state: { loading: boolean } = {
        loading: false,
    };

    windowOnloadHandler: () => {};

    static fetchData(store: Store<types.StoreState>, match: match<RouteMatchProps>, queryParams?: { [s: string]: string | [] }) {
        console.log('triggering VenueProfileContainer fetch data');
        const id = parseIdFromMatch(match);
        if (!!id) {
            // the query params passed here are the query params passed via URL which
            // may not necessarily match to the queryParams required by some of the API calls
            // so we need to run the transformation here
            let transformedParams = queryParams || {};
            if (!!queryParams && !!queryParams.showUnapprovedImages) {
                transformedParams.show_unapproved_images = queryParams.showUnapprovedImages;
                delete queryParams.showUnapprovedImages;
            }

            console.log('triggering loadVenueDetailsDispatcher fetch data');

            const loadVenueDetails = loadVenueDetailsDispatcher(store.dispatch, id, transformedParams);
            return Promise.all([loadVenueDetails]);
        }
        return Promise.resolve();
    }

    constructor(props: Props) {
        super(props);
        this.onClickFavorite = this.onClickFavorite.bind(this);
        this.onImageScroll = this.onImageScroll.bind(this);
        this.onAccordionExpanded = this.onAccordionExpanded.bind(this);
        this.onWebsiteClick = this.onWebsiteClick.bind(this);
        this.onPhoneClick = this.onPhoneClick.bind(this);
        this.onCallClick = this.onCallClick.bind(this);
        this.onSocialClick = this.onSocialClick.bind(this);
        this.onInquirySubmit = this.onInquirySubmit.bind(this);
        this.onInquiryModalRecClick = this.onInquiryModalRecClick.bind(this);
        this.onRecommendationClick = this.onRecommendationClick.bind(this);
        this.handleNavigatedToConnectAccount = this.handleNavigatedToConnectAccount.bind(this);
        this.handleConnectAccountSuccess = this.handleConnectAccountSuccess.bind(this);
        this.handleNavigatedToDWTInquiryFormStep2 = this.handleNavigatedToDWTInquiryFormStep2.bind(this);
        this.showInquiryModal = this.showInquiryModal.bind(this);
        this.processRouteActions = this.processRouteActions.bind(this);
        this.isRdvProfile = this.isRdvProfile.bind(this);
    }

    logVisitedEvent() {
        console.log('logging visitedEvent');
        const match = this.props.match;
        const vid = !!match && match.params.id;
        if (!!vid) {
            // Log ab test props
            const testLbl = UserProp.TESTLBL_PROFILE_VERSION;
            const props = {};
            props[testLbl] = 'v2';
            props[UserProp.TESTLBL_TEST_FLAG_DETAILS] = this.props.testFlagDetails;
            props[UserProp.TESTLBL_TEST_GUEST_ROOMS_INTENT_CTA] = this.props.testGuestRoomsIntentCta === 'B' ? 'new-copy' : 'original-copy';
            props[UserProp.TESTLBL_TEST_SIMPLIFY_OUTREACH_2] = this.props.simplifyOutreachTestValue2;

            props[UserProp.TESTLBL_SHOW_TRAVEL_NOTIFICATIONS] = true;

            if (this.props.showAdditionalPriceVenueButton !== undefined) {
                props[UserProp.TESTLBL_ADD_PRICE_VENUE_BUTTON_TO_PROFILE] = this.props.showAdditionalPriceVenueButton;
            }

            setCurUserProps(props);

            logEvent(AnalyticsEvent.EVTNM_VISITED_WEDDING_VENUE_PROFILE, {
                [EventParams.VENUE_ID]: vid,
                'Is SPA': true,
                Variant: this.props.is_destination ? 'Destination' : 'Regular',
                'Inquiry Type': this.isRdvProfile() ? InquiryType.REHEARSAL_DINNER : InquiryType.WEDDING,
                Market: this.props.market,
                [EventParams.METRICS_METATPYE]: this.isRdvProfile()
                    ? Venues.VenueMetricsMetaType.RD_METRIC
                    : Venues.VenueMetricsMetaType.WEDDING_METRIC,
                'Test Flag': this.props.testFlagDetails,
                Listed: !this.props.is_not_available,
                'Shopping Cart Shown': this.props.showShoppingCart,
            });
        }
    }

    componentDidMount() {
        console.log('VenueProfileContainer ComponentDidMount, requesting data + dispatching actions');
        const venueIdFromRoute = parseIdFromMatch(this.props.match);
        const pageLoadCommon = () => {
            if (!!venueIdFromRoute) {
                // Load personalized data and refresh page data
                this.reloadPageData(venueIdFromRoute).then(() => {
                    // Trigger visited event
                    if (!IS_NODE_SERVER) {
                        this.logVisitedEvent();
                    }
                });

                // Trigger navigation event
                this.props.navigatedToVenueProfile(venueIdFromRoute);
            }

            this.processRouteActions();
        };

        if (!!venueIdFromRoute) {
            if (!this.props.profileLoaded) {
                // Hit the API for data then process actions
                this.props
                    .dispatchLoadVenueDetails(venueIdFromRoute, {
                        preview: this.hasPreviewQp() ? '1' : undefined,
                        show_unapproved_images: this.hasShowUnapprovedImagesQp() ? '1' : undefined,
                    })
                    .then(pageLoadCommon);
            } else {
                pageLoadCommon();
            }

            this.windowOnloadHandler = () => this.loadPostOnloadData(venueIdFromRoute);
            window.addEventListener('load', this.windowOnloadHandler);
        }
    }

    componentWillUnmount() {
        this.props.navigatedAwayFromVenueProfile();
        window.removeEventListener('load', this.windowOnloadHandler);
    }

    componentDidUpdate(prevProps: Props) {
        // Loading different venue
        const prevVenueId = !!prevProps.match && prevProps.match.params.id;
        const curVenueId = !!this.props.match && this.props.match.params.id;
        const parsedVenueId = parseIdFromMatch(this.props.match);
        const isChangingVenue = prevVenueId !== curVenueId && !!prevVenueId;
        if (isChangingVenue) {
            this.logVisitedEvent();
            parsedVenueId && this.reloadPageData(parsedVenueId);
            return;
        }

        // Auth status change
        const prevIsAuthed = prevProps.isAuthed;
        const curIsAuthed = this.props.isAuthed;
        const gotAuthenticated = !prevIsAuthed && curIsAuthed;
        if (gotAuthenticated && !!parsedVenueId) {
            this.reloadPageData(parsedVenueId);
            return;
        }

        // Render RDV profile
        if (this.isRdvProfile()) {
            this.props.renderRdvProfile();
        }

        // Log recommendations impressions once it loads for the first time
        if (
            prevProps.recommendationsLoadSucceeded === undefined &&
            this.props.recommendationsLoadSucceeded &&
            this.props.recommendations.length > 0
        ) {
            VendorsApi.logVenueRecomendationsImpression({
                venue_ids: this.props.recommendations.map((rec) => rec.id),
                venue_recommended_for_id: this.props.id,
                recommended_from: Venues.InventoryType.INV_SUBTYPE_REC_VENUEDETAILS,
                metrics_metatype: this.isRdvProfile() ? Venues.VenueMetricsMetaType.RD_METRIC : Venues.VenueMetricsMetaType.WEDDING_METRIC,
            });
        }

        // Set personalized props once
        if (prevProps.persinalizedInfoLoadSucceeded === undefined && this.props.persinalizedInfoLoadSucceeded) {
            let props = {};
            props[UserProp.TESTLBL_TEST_FLAG_PERSONALIZED] = this.props.testFlagPersonalized;
            setCurUserProps(props);
        }
    }

    mapImageToURL(image: VenueImageData) {
        // const storageURL = image.storage_url;
        let url = image.image;
        url = formatCventImage({ src: url, resize: '900x450', strict: true });
        if (image.landscape_image) {
            let imgSrc = image.landscape_image;
            url = formatCventImage({ src: imgSrc, resize: '900x450', strict: true });
        }

        if (image.portrait_image) {
            let imgSrc = image.portrait_image;
            url = formatCventImage({ src: imgSrc });
        }

        return {
            src: url,
            placeholderUrl: image.mini_thumbnail,
            credit: image.photo_credit,
        };
    }

    render() {
        const pageMainElement = this.renderPageMainElement();

        let meta = [];
        if (!!this.props.meta_description) {
            meta.push({
                name: 'description',
                content: `${this.props.meta_description}`,
            });
        }

        let links = [];
        if (!!this.props.canonical_url) {
            let canonical = {
                rel: 'canonical',
                href: this.props.canonical_url,
            };
            links.push(canonical);
        }

        let scripts = [];
        if (!!this.props.schema_org) {
            scripts.push({ type: 'application/ld+json', innerHTML: `${this.props.schema_org}` });
        }

        const pageTitle = this.props.page_title || '';

        return (
            <div>
                <Helmet meta={meta} title={pageTitle} script={scripts} link={links}>
                    <body className={cn({ [UNLISTED_VENUE_PAGE_APP_CLASS]: this.props.is_not_available }, VENUE_PAGE_APP_CLASS)} />
                </Helmet>
                {pageMainElement}
            </div>
        );
    }

    private onClickFavorite(target: boolean) {
        if (!this.props.isAuthed) {
            this.props.showSigninModal();
            return Promise.reject();
        }

        return new Promise<boolean>((resolve, reject) => {
            const venueId = this.props.id;
            const favoriteType = this.isRdvProfile() ? Venues.FavoriteType.REHEARSAL_DINNERS : Venues.FavoriteType.WEDDING;
            if (venueId) {
                if (target) {
                    this.props
                        .setFavorite(venueId, favoriteType)
                        .then(() => resolve(true))
                        .catch(() => reject());
                } else {
                    this.props
                        .unsetFavorite(venueId)
                        .then(() => resolve(false))
                        .catch(() => reject());
                }
            } else {
                reject();
            }
        });
    }

    private onRecommendationClick(position: number, recommendations: Venues.VenueRecommendation[]) {
        const venueId = recommendations[position].id;

        logEvent(AnalyticsEvent.EVTNM_CLICK_PROFILE_RECOMMENDATION, {
            [EventParams.POSITION]: position,
            [EventParams.LOCATION]: EventLocation.PROFILE,
            [EventParams.INQUIRY_TYPE]: this.isRdvProfile() ? InquiryType.REHEARSAL_DINNER : InquiryType.WEDDING,
            [EventParams.VENUE_ID]: venueId,
        });
    }

    private onWebsiteClick() {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_VENUE_WEBSITE, {
            [EventParams.VENUE_ID]: this.props.id,
            [EventParams.METRICS_METATPYE]: this.isRdvProfile()
                ? Venues.VenueMetricsMetaType.RD_METRIC
                : Venues.VenueMetricsMetaType.WEDDING_METRIC,
        });
    }

    private onCallClick() {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_SHOW_PHONE_NUMBER, {
            [EventParams.VENUE_ID]: this.props.id,
            [EventParams.METRICS_METATPYE]: this.isRdvProfile()
                ? Venues.VenueMetricsMetaType.RD_METRIC
                : Venues.VenueMetricsMetaType.WEDDING_METRIC,
        });
    }

    private onPhoneClick() {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_CALL_PHONE, { [EventParams.VENUE_ID]: this.props.id });
    }

    private onSocialClick(network: 'facebook' | 'instagram' | 'pinterest') {
        logEvent(AnalyticsEvent.EVTNM_PROFILE_CLICKED_SOCIAL, { Network: network });
    }

    private onImageScroll(index: number) {
        // Only track event on odd indexed events
        if (index % 2 === 1) {
            logEvent(AnalyticsEvent.EVTNM_PROFILE_PHOTO_SCROLLED, { Index: index });
        }
    }

    private onAccordionExpanded(name: string) {
        logEvent(AnalyticsEvent.EVTNM_PROFILE_ACCORDION_EXPANDED, { Name: name });
    }

    private reloadPageData(venueId: number): Promise<void[]> {
        this.setState({ loading: true });
        const reloadPromises = [
            // Always load personalized content on mount, we skip this durring ssr
            this.props.dipsatchLoadPersonalizedDetails(venueId),
            // Reload breadcrumbs
            this.props.getVenueHeaderData(venueId, this.hasRdvQp()),
            this.props.dispatchLoadVenueDetails(venueId, {
                preview: this.hasPreviewQp() ? '1' : undefined,
                show_unapproved_images: this.hasShowUnapprovedImagesQp() ? '1' : undefined,
                rdv: this.hasRdvQp() ? '1' : undefined,
            }),
        ];

        return Promise.all(reloadPromises).then((res) => {
            this.setState({ loading: false });
            return res;
        });
    }

    /**
     * Load data that shouldn't block the browser's onload event
     * @param venueId the venue to load data for
     */
    private loadPostOnloadData(venueId: number): Promise<void[]> {
        let visitedVenues = LocalStorage.getItem(StorableDataKeys.VISITED_VENUES);

        if (!visitedVenues) {
            LocalStorage.setItem(StorableDataKeys.VISITED_VENUES, []);
            visitedVenues = [];
        }

        if (!visitedVenues.includes(venueId)) {
            visitedVenues = [...visitedVenues, venueId];
            LocalStorage.setItem(StorableDataKeys.VISITED_VENUES, visitedVenues);
        }
        const reloadPromises = [this.props.loadRecommendations(venueId, this.hasRdvQp(), [...visitedVenues, venueId])];

        // Fetch unapproved images if in preview mode
        if (this.hasPreviewQp() || this.hasShowUnapprovedImagesQp() || this.hasRdvQp()) {
            reloadPromises.push(
                this.props.dispatchLoadVenueDetails(venueId, {
                    preview: this.hasPreviewQp() ? '1' : undefined,
                    show_unapproved_images: this.hasShowUnapprovedImagesQp() ? '1' : undefined,
                    rdv: this.hasRdvQp() ? '1' : undefined,
                })
            );
        }

        return Promise.all(reloadPromises);
    }

    private onInquirySubmit(isQuote: boolean) {
        logEvent(isQuote ? AnalyticsEvent.EVTNM_SUBMIT_QUOTE : AnalyticsEvent.EVTNM_SUBMIT_INQUIRY, {
            'Venue ID': this.props.id,
            Source: 'profile',
            'Inquiry Type': this.isDWProfile()
                ? 'Destination Weddings'
                : this.isRdvProfile()
                ? InquiryType.REHEARSAL_DINNER
                : InquiryType.WEDDING,
        });
    }

    private onInquiryModalRecClick(position: number, recommendations: Venues.VenueRecommendation[]) {
        logEvent(AnalyticsEvent.EVTNM_CLICK_PROFILE_RECOMMENDATION, {
            [EventParams.POSITION]: position,
            [EventParams.LOCATION]: EventLocation.INQUIRY_CONFIRMATION,
            [EventParams.INQUIRY_TYPE]: this.isRdvProfile() ? InquiryType.REHEARSAL_DINNER : InquiryType.WEDDING,
            [EventParams.VENUE_ID]: recommendations[position].id,
        });
    }

    private showInquiryModal(isRDVInquiry?: boolean) {
        this.props.showInquiryModal();
        const { is_priced, is_destination } = this.props;
        logEvent(is_priced ? AnalyticsEvent.EVTNM_VISITED_INQUIRY_FORM : AnalyticsEvent.EVTNM_VISITED_QUOTE_FORM, {
            Variant: is_destination ? 'Destination Weddings' : undefined,
            Screen: is_destination ? 1 : undefined,
            Source: 'profile',
            'Inquiry Type': this.isRdvProfile() || !!isRDVInquiry ? InquiryType.REHEARSAL_DINNER : InquiryType.WEDDING,
        });
    }

    private handleNavigatedToDWTInquiryFormStep2() {
        logEvent(AnalyticsEvent.EVTNM_VISITED_QUOTE_FORM, {
            Variant: 'Destination Weddings',
            Screen: 2,
        });
    }

    private processRouteActions() {
        const action = !!this.props.match && this.props.match.params.action;

        // Show inquiry modal if route had a valid action
        const urlHasAction =
            (Object as any)
                .keys(VenueProfileRouteAction)
                .map((k: string) => VenueProfileRouteAction[k])
                .indexOf(action) !== -1;
        const qps = querystring.parse(this.props.location.search);
        const urlHasActionQp = qps.request_quote === '1';

        if (urlHasAction || urlHasActionQp) {
            this.showInquiryModal();
        }
    }

    private handleNavigatedToConnectAccount() {
        logEvent(AnalyticsEvent.EVTNM_VISITED_INQUIRY_REG_MODAL, {});
    }

    private handleNavigatedToPostOutreachScreen() {
        logEvent(AnalyticsEvent.EVTNM_VISITED_POST_OUTREACH, {
            Source: 'web',
        });
        analytics.setCurUserProps({ name: analytics.ClarityTags.POST_OUTREACH_FLOW, value: 'Yes' });
    }

    private handleConnectAccountSuccess() {
        this.props.tryGetUserInfo();
        logEvent(AnalyticsEvent.EVTNM_REGISTERED, {
            'Is Mobile': isMobile,
        });
    }

    private hasRdvQp() {
        const qps = querystring.parse(this.props.location.search);
        return qps.rdv === '1';
    }

    private hasAddPriceButtonQp() {
        const qps = querystring.parse(this.props.location.search);
        if (qps.add_price_button === '1') return true;
        else if (qps.add_price_button === '0') return false;
        else return undefined;
    }

    private isRdvProfile() {
        const urlHasRdvQp = this.hasRdvQp();
        return this.props.has_rds && urlHasRdvQp;
    }

    private isDWProfile() {
        return this.props.market === 'Destination Weddings';
    }

    private hasAdditionalPriceButton() {
        const urlHasAddPriceButtonQp = this.hasAddPriceButtonQp();
        if (urlHasAddPriceButtonQp === undefined) {
            return this.props.showAdditionalPriceVenueButton;
        } else {
            return urlHasAddPriceButtonQp;
        }
    }

    private hasPreviewQp() {
        const qps = querystring.parse(this.props.location.search);
        return qps.preview === '1';
    }

    private hasShowUnapprovedImagesQp() {
        return querystring.parse(this.props.location.search).showUnapprovedImages === '1';
    }

    private inquiryModalCloseClicked(isQuote: boolean, screen?: string) {
        logEvent(AnalyticsEvent.EVTNM_INQUIRY_MODAL_CLOSE_CLICKED, {
            Variant: isQuote ? 'Quote' : 'Inquiry',
            Screen: screen || null,
        });
    }

    private pushOutreachNoThanksClicked() {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_POST_OUTREACH_NO_THANKS, {});
    }

    private pushOutreachInquireClicked(positions: number[]) {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_POST_OUTREACH_INQUIRE, {
            Source: 'web',
            Position: positions,
            'Number of venues': positions.length,
        });
    }

    private pushIntentCTAonPostOutreachStep1() {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_POST_OUTREACH_STEP_1, {
            Source: 'post outreach flow step 1',
        });
    }

    private pushOutreachSuccessBrowseMoreClicked() {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_POST_OUTREACH_SUCCESS_BROWSE_MORE, {});
    }

    private abandonmentShown(isQuote: boolean) {
        LocalStorage.setItem(StorableDataKeys.INQUIRY_MODAL_ABANDONMENT_SHOWN, true);
        logEvent(AnalyticsEvent.EVTNM_INQUIRY_MODAL_ABANDONMENT_SHOWN, {
            Variant: isQuote ? 'Quote' : 'Inquiry',
        });
    }

    private abandonmentCtaClicked(isQuote: boolean) {
        logEvent(AnalyticsEvent.EVTNM_INQUIRY_MODAL_ABANDONMENT_CTA, {
            Variant: isQuote ? 'Quote' : 'Inquiry',
        });
    }

    private abandonmentDismissed(isQuote: boolean) {
        logEvent(AnalyticsEvent.EVTNM_INQUIRY_MODAL_ABANDONMENT_DISMISSED, {
            Variant: isQuote ? 'Quote' : 'Inquiry',
        });
    }

    private shouldShowAbandonment() {
        return !LocalStorage.getItem(StorableDataKeys.INQUIRY_MODAL_ABANDONMENT_SHOWN);
    }

    private handleNavigatedToSeeNearbyHotels(testGuestRoomsIntentCta?: 'A' | 'B') {
        logEvent(AnalyticsEvent.EVTNM_VISITED_SEE_NEARBY_HOTELS, {
            Variant: testGuestRoomsIntentCta === 'B' ? 'new-copy' : 'original-copy',
        });
    }

    private handleNavigatedToHotels(count: number) {
        logEvent(AnalyticsEvent.EVTNM_VISITED_HOTEL_RECOMMENDATIONS, {
            'Number of hotels shown': count,
            Source: 'post outreach flow',
        });
    }

    private handleRejectSeeHotels() {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_NO_HOTEL_RECOMMENDATIONS, {
            Source: 'post outreach success',
        });
    }

    private handleRejectHotelsRecommended() {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_NO_HOTEL_RECOMMENDATIONS, {
            Source: 'hotel rooms recommended',
        });
    }

    private handleHotelsRecommendedRFP() {
        logEvent(AnalyticsEvent.EVTNM_VISITED_GR_RFP_FORM, {});
    }

    handleHotelsRecommendedForm(source: string, count: number) {
        logEvent(AnalyticsEvent.EVTNM_SUBMIT_RFP, {
            source,
            'Number of hotels': count,
        });
    }

    handleRoomCountSubmit(props: object) {
        logEvent(AnalyticsEvent.EVTNM_SUBMITTED_ROOM_COUNT, props);
    }

    handleClickedSpecifyGuestRooms(source?: string) {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_SPECIFY_GUEST_ROOMS, { Source: source });
    }

    private isEmailExists() {
        logEvent(AnalyticsEvent.EVTNM_SHOWN_EMAIL_ALREADY_EXISTS, {
            Source: 'Contact venue modal',
        });
    }

    private handleAddVenueToShoppingCart() {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_SELECT_VENUE_CTA, {
            Source: 'Venue Profile',
        });
    }

    private handleRemoveVenueFromShoppingCart() {
        logEvent(AnalyticsEvent.EVTNM_CLICKED_REMOVE_FROM_LIST_CTA, {
            Source: 'Venue Profile',
        });
    }

    private renderPageMainElement(): JSX.Element {
        const {
            slideToNextImage,
            slideToPrevImage,
            showPhone,
            setActiveTab,
            openMoreTab,
            closeMoreTab,
            setFavorite,
            unsetFavorite,
            capacity,
            services,
            services_rds,
            isAuthed,
            has_rds,
            has_weddings,
            is_not_available,
            ...props
        } = this.props;

        // not ready, show spinner
        if (!props.id || this.state.loading) {
            return (
                <BlurryLoader>
                    <LoadingSpinner />
                </BlurryLoader>
            );
        }

        // Venue is unlisted but not hidden, show unlisted venue page
        if (!!is_not_available) {
            return (
                <UnlistedVenuePage
                    venueName={props.name}
                    recommendations={props.recommendations}
                    recommendationsLoadSucceeded={props.recommendationsLoadSucceeded}
                    userIsAuthed={isAuthed}
                    market={props.market}
                    region={props.region}
                />
            );
        }

        // Venue has no services to display
        const hasError = (!has_weddings && !has_rds) || (!has_weddings && !this.hasRdvQp()) || (!has_rds && this.hasRdvQp());

        if (hasError) {
            // TODO probably need return 404 from SSR, but I dont know how to repro and test it
            return <div style={{ padding: '15px' }}>Something wrong with venue, plz try open another</div>;
        }

        const servicesGroup = services.reduce((prev, curr) => {
            if (curr.service_type !== VenueService.cocktailHour && curr.service_type !== VenueService.privateCeremony) {
                prev[curr.service_type] = curr.capacity;
            } else {
                prev[curr.service_type] = 1;
            }
            return prev;
        }, {});

        const servicesList: string[] = [];

        if (!!servicesGroup[types.VenueService.indoorCeremony] && !!servicesGroup[types.VenueService.outdoorCeremony]) {
            servicesList.push('Indoor/Outdoor Ceremony');
        } else if (!!servicesGroup[types.VenueService.indoorCeremony]) {
            servicesList.push('Indoor Ceremony');
        } else if (!!servicesGroup[types.VenueService.outdoorCeremony]) {
            servicesList.push('Outdoor Ceremony');
        }

        if (!!servicesGroup[types.VenueService.indoorReception] && !!servicesGroup[types.VenueService.outdoorReception]) {
            servicesList.push('Indoor/Outdoor Reception');
        } else if (!!servicesGroup[types.VenueService.indoorReception]) {
            servicesList.push('Indoor Reception');
        } else if (!!servicesGroup[types.VenueService.outdoorReception]) {
            servicesList.push('Outdoor Reception');
        }

        if (servicesGroup[types.VenueService.cocktailHour]) {
            servicesList.push('Cocktail Hour');
        }

        if (servicesGroup[types.VenueService.privateCeremony]) {
            servicesList.push('Private Ceremony');
        }

        // Process services
        const serviceProps = services.map((item) => {
            const type = item.service_type;
            let typeProp;
            if (IndoorVenueServiceTypes.indexOf(type) > -1) {
                typeProp = 'indoor';
            } else if (OutdoorVenueServiceTypes.indexOf(type) > -1) {
                typeProp = 'outdoor';
            }

            if (!typeProp) {
                return;
            }

            let catProp;
            if (CeremonyServiceTypes.indexOf(type) > -1) {
                catProp = 'ceremony';
            } else if (ReceptionServiceTypes.indexOf(type) > -1) {
                catProp = 'reception';
            }

            if (!catProp) {
                return;
            }

            return {
                category: catProp,
                type: typeProp,
                capacity: item.capacity,
            };
        });

        const rdServiceProps = services_rds.map((item) => {
            const type = item.service_type;
            let typeProp;
            if (IndoorVenueServiceTypes.indexOf(type) > -1) {
                typeProp = 'indoor';
            } else if (OutdoorVenueServiceTypes.indexOf(type) > -1) {
                typeProp = 'outdoor';
            }

            if (!typeProp) {
                return;
            }

            return {
                category: 'rd',
                type: typeProp,
                capacity: item.capacity,
            };
        });

        let ceremonyServiceProps = serviceProps.filter((item) => item && item.category === 'ceremony');
        let receptionServiceProps = serviceProps.filter((item) => item && item.category === 'reception');

        // Only show ceremony/reception options if the venue has weddings
        if (!this.props.has_weddings) {
            ceremonyServiceProps = [];
            receptionServiceProps = [];
        }

        // Compute min/max capacity
        const parsedCaps = services.map((s) => s.capacity);
        let minCapacity: number | undefined;
        if (parsedCaps.length > 0) {
            minCapacity = Math.min(...parsedCaps);
        }

        return (
            <VenueProfile
                sessionId={'test'}
                name={this.props.name}
                country={this.props.country}
                onAccordionExpanded={this.onAccordionExpanded}
                province={this.props.province}
                zip_code={this.props.zip_code}
                is_destination={this.props.is_destination}
                renderRdv={this.props.renderRdv}
                areAccordionsOpen={true}
                // Slider props -------
                videos={props.videos.map(({ embedded_video }) => embedded_video)}
                activeImageIndex={props.activeSlide}
                onNextSlide={slideToNextImage}
                onPrevSlide={slideToPrevImage}
                images={props.images.map(this.mapImageToURL)}
                onActiveImageChange={this.onImageScroll}
                // End slider props

                // Venue details
                venueId={props.id}
                venueCSNId={props.csn_venue_id}
                venueRegion={props.region}
                venueMarket={props.market}
                isPriced={props.is_priced}
                isDestination={props.is_destination}
                startingPricePerPersonRDs={this.props.starting_price_per_person_rds}
                description={this.props.description}
                venueNotes={props.rental_fee_description} // in models venue notes are labeled so
                styles={props.styles.map((s) => ({ id: s.id, name: s.long_name }))}
                /*
                    Hacky casting, the filter ensures no undefined values get through but
                    typescript type inference is limited
                */
                ceremonies={ceremonyServiceProps as ServiceProps[]}
                receptions={receptionServiceProps as ServiceProps[]}
                rds={rdServiceProps as ServiceProps[]}
                amenities={props.amenities}
                restrictions={props.special_restrictions}
                maxCapacity={props.max_capacity}
                minCapacity={minCapacity}
                availability={props.availability}
                availabilityLastUpdated={props.last_updated_date}
                highlights={props.venueHighlights}
                phoneNumber={props.is_destination ? undefined : props.phone_number}
                onPhoneClick={this.onPhoneClick}
                onCallClick={this.onCallClick}
                discounts={props.discounts}
                upcoming_events={props.upcoming_events}
                // Awards
                awards={props.awards}
                // Address
                // TODO: Handle this when DWT profile updates are made
                city={props.city || ''}
                state={props.state || ''}
                address_1={props.address_1}
                address_2={props.address_2}
                // Map
                lng={props.longitude}
                ltd={props.latitude}
                // Budget
                budgetMin={!!props.budgetRangeMin ? props.budgetRangeMin : undefined}
                budgetCapacity={!!props.budgetCapacity ? props.budgetCapacity : undefined}
                hideWeddingCosts={props.hide_wedding_costs}
                hideGuestRooms={props.hide_guest_rooms}
                // Estimate
                spotEstimateTotal={!!props.userSpotEstimate ? props.userSpotEstimate : undefined}
                // Personalized
                showAdditionalPriceVenueButton={this.hasAdditionalPriceButton()}
                showGuestRoomsRecommendations={props.showGuestRoomsRecommendations}
                showWsRfpForm={props.showWsRfpForm}
                testGuestRoomsIntentCta={props.testGuestRoomsIntentCta}
                intentCTAonPostOutreachStep1={props.intentCTAonPostOutreachStep1}
                intentCTAStepRemoved={props.intentCTAStepRemoved}
                showSleepingRoomsOnVenueProfile={props.showSleepingRoomsOnVenueProfile}
                // Notifications
                notifications={props.notifications}
                // Favorites
                isFavorite={props.is_favorite || false}
                onFavoriteClick={this.onClickFavorite}
                // Subheader nav
                userIsAuthed={isAuthed}
                // Links
                websiteUrl={props.website}
                onWebsiteClick={this.onWebsiteClick}
                facebookUrl={props.facebookLink || undefined}
                instagramUrl={props.instagramLink || undefined}
                pinterestUrl={props.pinterestLink || undefined}
                onSocialClick={this.onSocialClick}
                // Recommendations
                disableRecommendations={props.disableRecommendations}
                recommendations={props.recommendations}
                recommendationsLoadSucceeded={props.recommendationsLoadSucceeded}
                onRecommendationClick={this.onRecommendationClick}
                // Packages
                packages={props.packages.map((pkg) => ({
                    id: pkg.id,
                    title: pkg.title,
                    startingPriceText: pkg.starting_price,
                    description: pkg.description,
                    contentLink: pkg.content_link,
                }))}
                // Inquiry Modal
                inquiryModalShowing={this.props.inquiryModalShowing}
                showInquiryModal={this.showInquiryModal}
                shouldShowInquiryAbandonment={this.shouldShowAbandonment}
                hideInquiryModal={this.props.hideInquiryModal}
                handleNavigatedToDWTInquiryFormStep2={this.handleNavigatedToDWTInquiryFormStep2}
                handleNavigatedToConnectAccount={this.handleNavigatedToConnectAccount}
                handleNavigatedToPostOutreachScreen={this.handleNavigatedToPostOutreachScreen}
                inquiryFormConnectAccountSuccess={this.handleConnectAccountSuccess}
                inquiryFormSubmitSuccess={this.onInquirySubmit}
                inquiryModalRecommendationClicked={this.onInquiryModalRecClick}
                handleCloseClicked={(screen: string) => this.inquiryModalCloseClicked(!props.is_priced, screen)}
                pushOutreachNoThanksClicked={this.pushOutreachNoThanksClicked}
                pushOutreachSuccessBrowseMoreClicked={this.pushOutreachSuccessBrowseMoreClicked}
                pushOutreachInquireClicked={this.pushOutreachInquireClicked}
                pushIntentCTAonPostOutreachStep1={this.pushIntentCTAonPostOutreachStep1}
                handleAbandonmentShown={() => this.abandonmentShown(!props.is_priced)}
                handleAbandonmentCtaClicked={() => this.abandonmentCtaClicked(!props.is_priced)}
                handleAbandonmentDismissed={() => this.abandonmentDismissed(!props.is_priced)}
                simplifyOutreachTestValue2={this.props.simplifyOutreachTestValue2}
                handleNavigatedToSeeNearbyHotels={this.handleNavigatedToSeeNearbyHotels}
                handleNavigatedToHotels={this.handleNavigatedToHotels}
                handleRejectSeeHotels={this.handleRejectSeeHotels}
                handleRejectHotelsRecommended={this.handleRejectHotelsRecommended}
                handleHotelsRecommendedRFP={this.handleHotelsRecommendedRFP}
                handleHotelsRecommendedForm={this.handleHotelsRecommendedForm}
                handleRoomCountSubmit={this.handleRoomCountSubmit}
                handleClickedSpecifyGuestRooms={this.handleClickedSpecifyGuestRooms}
                handleAddVenueToShoppingCart={this.handleAddVenueToShoppingCart}
                handleRemoveVenueFromShoppingCart={this.handleRemoveVenueFromShoppingCart}
                isEmailExists={this.isEmailExists}
                userInfo={this.props.userInfo || undefined}
                // Shopping Cart
                showShoppingCart={this.props.showShoppingCart}
            />
        );
    }
}

const VenueProfileContainer = (props: Props) => {
    const { user, userIsAuthenticated, openAuthModal } = React.useContext(WSAppContext);
    const { loadHeaderData } = React.useContext(HeaderContext);
    const showShoppingCart = false;

    return (
        <_VenueProfileContainer
            {...{
                ...props,
                userInfo: user,
                isAuthed: userIsAuthenticated,
                getVenueHeaderData: loadHeaderData,
                showSigninModal: openAuthModal,
                showShoppingCart,
            }}
        />
    );
};

// This is needed for SSR to work properly
VenueProfileContainer.fetchData = _VenueProfileContainer.fetchData;

export default connect(mapStateToProps, mapDispatchToProps)(VenueProfileContainer);
